/**
 * Direction A — CRM Operator design tokens.
 *
 * Mi6 brand-locked palette + spacing primitives shared by every operator
 * component. Import from one place so we never drift across pages.
 */

export const tokens = {
  color: {
    // Locked palette (master)
    black:       "#0B0B0F",
    charcoal:    "#1F1F23",
    softWhite:   "#FAFAF7",
    cardBg:      "#F5F5F4",
    helixBg:     "#0A0A0C",
    // Legacy aliases — DO NOT USE for new work; values are pure black, not navy.
    navy:        "#0B0B0F",
    navySoft:    "#1F1F23",
    navyHover:   "#2A2A2E",
    red:         "#DC2626",
    redHover:    "#B91C1C",
    canvas:      "#FFFFFF",
    surfaceMute: "#FAFAF7",
    border:      "#E5E7EB",
    borderSoft:  "#EFEFEE",
    textPrimary: "#0B0B0F",
    textMuted:   "#6B6B70",
    textSubtle:  "#8E8E94",
    success:     "#16A34A",
    successBg:   "#DCFCE7",
    amber:       "#EAB308",
    amberBg:     "#FEF3C7",
    danger:      "#DC2626",
    dangerBg:    "#FEE2E2",
    info:        "#0B0B0F",
    infoBg:      "#EFEFEE",
  },
  font: {
    display: "'DM Sans', ui-sans-serif, system-ui, -apple-system, sans-serif",
    body:    "Inter, ui-sans-serif, system-ui, -apple-system, sans-serif",
    mono:    "'Geist Mono', ui-monospace, monospace",
  },
  space: {
    sidebarWidth: 200,
    sidebarCollapsedWidth: 56,
    topbarHeight: 64,
    contextTabsHeight: 40,
    pagePad: 20,
    rowPad: 8,
  },
  radius: {
    card: 8,
    pill: 999,
  },
  shadow: {
    card: "0 1px 2px rgba(15, 23, 42, 0.04)",
  },
} as const;

// Stage pill mapping. Add new stages here — components read from this map.
export type StageKey =
  | "Prospecting"
  | "Evaluating"
  | "Proposal"
  | "Closing"
  | "Stalled"
  | "Won"
  | "Lost";

export const stageStyles: Record<string, { fg: string; bg: string; dashed?: boolean }> = {
  Prospecting: { fg: "#0B0B0F", bg: "#EFEFEE" },
  Evaluating:  { fg: "#92400E", bg: "#FEF3C7" },
  Proposal:    { fg: "#9A1F1F", bg: "#FEE2E2" },
  Closing:     { fg: "#166534", bg: "#DCFCE7" },
  Stalled:     { fg: "#9A1F1F", bg: "#FEE2E2", dashed: true },
  Won:         { fg: "#166534", bg: "#DCFCE7" },
  Lost:        { fg: "#525258", bg: "#EFEFEE" },
};

export function getStageStyle(stage: string | null | undefined) {
  if (!stage) return { fg: "#525258", bg: "#EFEFEE" };
  return stageStyles[stage] ?? { fg: "#525258", bg: "#EFEFEE" };
}
